import { useMemo } from "react";
import {
    QueryClient,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import { documentCategoryContract } from "@common/contracts/document-category.contract";
import { DocumentCategory } from "@common/types/documentCategory";
import { isUncategorizedDocumentTaxonomy } from "@common/helpers/documentCategoryFilter";
import { useScope } from "@state/scopeContext.lib";
import { getBackendAPIClient } from "@lib/backendAPIClient";

const documentCategoryClient = getBackendAPIClient(documentCategoryContract);

const DOCUMENT_CATEGORIES_STALE_TIME = 1000 * 60 * 5;

const documentCategoryQueryKeys = {
    all: () => ["documentCategories"] as const,
    query: (workspaceId: string) =>
        [...documentCategoryQueryKeys.all(), workspaceId] as const,
};

export function useDocumentCategories() {
    const { workspaceId, activeWorkspaceKey } = useScope();

    return useQuery({
        queryKey: documentCategoryQueryKeys.query(activeWorkspaceKey),
        queryFn: async () => {
            if (!workspaceId) {
                return undefined;
            }

            const response = await documentCategoryClient.getDocumentCategories(
                {
                    params: {
                        workspaceId,
                    },
                },
            );

            return response.body;
        },
        staleTime: DOCUMENT_CATEGORIES_STALE_TIME,
    });
}

export function useCreateDocumentCategoryMutation() {
    const queryClient = useQueryClient();
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof documentCategoryContract.createDocumentCategory
            >["body"],
        ) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }

            const response =
                await documentCategoryClient.createDocumentCategory({
                    params: { workspaceId },
                    body,
                });

            return response.body;
        },
        onSuccess: (createdCategory) => {
            addDocumentCategoryToQueryData(queryClient, createdCategory);
        },
    });
}

export function useUpdateDocumentCategoryMutation(category: DocumentCategory) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof documentCategoryContract.updateDocumentCategory
            >["body"],
        ) => {
            if (!category.workspaceId) {
                throw new Error("Can't update standard document category");
            }

            const response =
                await documentCategoryClient.updateDocumentCategory({
                    params: {
                        workspaceId: category.workspaceId,
                        categoryId: category.id,
                    },
                    body,
                });

            return response.body;
        },
        onMutate: async (body) => {
            await queryClient.cancelQueries({
                queryKey: documentCategoryQueryKeys.all(),
            });

            updateDocumentCategoryInQueryData(queryClient, {
                ...category,
                ...body,
            });
            return category;
        },
        onError: (_, __, originalCategory) => {
            if (originalCategory) {
                updateDocumentCategoryInQueryData(
                    queryClient,
                    originalCategory,
                );
            }
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({
                queryKey: documentCategoryQueryKeys.all(),
            });
        },
    });
}

export function useDeleteDocumentCategoryMutation(category: DocumentCategory) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            if (!category.workspaceId) {
                throw new Error("Can't delete standard document category");
            }

            await documentCategoryClient.deleteDocumentCategory({
                params: {
                    workspaceId: category.workspaceId,
                    categoryId: category.id,
                },
            });
        },
        onMutate: async () => {
            await queryClient.cancelQueries({
                queryKey: documentCategoryQueryKeys.all(),
            });

            removeDocumentCategoryFromQueryData(queryClient, category);
        },
        onError: () => {
            addDocumentCategoryToQueryData(queryClient, category);
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({
                queryKey: documentCategoryQueryKeys.all(),
            });
        },
    });
}

export function useFlatDocumentCategories(): DocumentCategory[] {
    const { data: documentCategoriesData } = useDocumentCategories();
    return useMemo(
        () =>
            (documentCategoriesData?.categories ?? []).flatMap(
                (category: DocumentCategory) => [
                    category,
                    ...(category.subcategories ?? []),
                ],
            ),
        [documentCategoriesData?.categories],
    );
}

export function useAssignableDocumentCategories(): DocumentCategory[] {
    const flatCategories = useFlatDocumentCategories();

    return useMemo(
        () =>
            flatCategories.filter(
                (category) =>
                    !isUncategorizedDocumentTaxonomy(category.documentTaxonomy),
            ),
        [flatCategories],
    );
}

export function useDocumentCategoryLabel(
    categoryIds: string[] | undefined,
): string | null {
    const allCategories = useFlatDocumentCategories();
    return useMemo(() => {
        if (!categoryIds?.length || allCategories.length === 0) {
            return null;
        }
        return (
            allCategories.find((c) => c.id === categoryIds[0])?.label ?? null
        );
    }, [categoryIds, allCategories]);
}

function addDocumentCategoryToQueryData(
    queryClient: QueryClient,
    createdCategory: DocumentCategory,
) {
    queryClient.setQueryData(
        documentCategoryQueryKeys.query(createdCategory.workspaceId ?? ""),
        (oldData: { categories: DocumentCategory[] } | undefined) => {
            if (!oldData) {
                return undefined;
            }

            return {
                categories: oldData.categories.map((category) =>
                    category.id === createdCategory.parentCategoryId
                        ? {
                              ...category,
                              subcategories: [
                                  ...(category.subcategories ?? []),
                                  createdCategory,
                              ],
                          }
                        : category,
                ),
            };
        },
    );
}

function removeDocumentCategoryFromQueryData(
    queryClient: QueryClient,
    removedCategory: DocumentCategory,
) {
    queryClient.setQueryData(
        documentCategoryQueryKeys.query(removedCategory.workspaceId ?? ""),
        (oldData: { categories: DocumentCategory[] } | undefined) => {
            if (!oldData) {
                return undefined;
            }

            return {
                categories: oldData.categories.map((category) =>
                    category.id === removedCategory.parentCategoryId
                        ? {
                              ...category,
                              subcategories:
                                  category.subcategories?.filter(
                                      (subcategory) =>
                                          subcategory.id !== removedCategory.id,
                                  ) ?? [],
                          }
                        : category,
                ),
            };
        },
    );
}

function updateDocumentCategoryInQueryData(
    queryClient: QueryClient,
    updatedCategory: DocumentCategory,
) {
    queryClient.setQueryData(
        documentCategoryQueryKeys.query(updatedCategory.workspaceId ?? ""),
        (oldData: { categories: DocumentCategory[] } | undefined) => {
            if (!oldData) {
                return undefined;
            }

            return {
                categories: oldData.categories.map((c) => ({
                    ...c,
                    subcategories:
                        c.subcategories?.map((subcategory) =>
                            subcategory.id === updatedCategory.id
                                ? updatedCategory
                                : subcategory,
                        ) ?? [],
                })),
            };
        },
    );
}
